import _formatErrorMessage from "@mui/x-internals/formatErrorMessage";
import _extends from "@babel/runtime/helpers/esm/extends";
import { createSelector, createSelectorMemoized } from '@base-ui/utils/store';
import { resolveResourceProperty } from "./schedulerResourceSelectors.mjs";
import { DEFAULT_EVENT_CREATION_CONFIG } from "../constants/index.mjs";
const processedEventSelector = createSelector(state => state.processedEventLookup, (processedEventLookup, eventId) => eventId == null ? null : processedEventLookup.get(eventId));
const isEventReadOnlySelector = createSelector((state, eventId) => {
  const processedEvent = processedEventSelector(state, eventId);
  if (!processedEvent) {
    return false;
  }
  return resolveEventProperty({
    state,
    resourceId: processedEvent.resource,
    valueInEvent: processedEvent.modelInBuiltInFormat?.readOnly,
    getValueInResource: r => r.areEventsReadOnly,
    valueInState: state.readOnly ?? false
  });
});
export const schedulerEventSelectors = {
  creationConfig: createSelectorMemoized(state => state.readOnly, state => state.eventCreation, (isSchedulerReadOnly, creationConfig) => {
    if (isSchedulerReadOnly) {
      return false;
    }
    if (creationConfig === false) {
      return false;
    }
    if (creationConfig === true) {
      return DEFAULT_EVENT_CREATION_CONFIG;
    }
    return _extends({}, DEFAULT_EVENT_CREATION_CONFIG, creationConfig);
  }),
  /**
   * Gets the default duration (in minutes) for newly created events.
   * This can be used when you need the value event on read-only calendar.
   */
  defaultEventDuration: createSelector(state => state.eventCreation, eventCreation => {
    if (typeof eventCreation === 'boolean') {
      return DEFAULT_EVENT_CREATION_CONFIG.duration;
    }
    return eventCreation?.duration ?? DEFAULT_EVENT_CREATION_CONFIG.duration;
  }),
  processedEvent: processedEventSelector,
  processedEventRequired: createSelector(processedEventSelector, (event, eventId) => {
    if (!event) {
      throw new Error(process.env.NODE_ENV !== "production" ? `MUI X Scheduler: Event with id="${eventId}" was not found. ` + 'The requested event does not exist in the scheduler state. ' + 'Verify the event id is correct and the event has been added.' : _formatErrorMessage(210, eventId));
    }
    return event;
  }),
  isReadOnly: isEventReadOnlySelector,
  color: createSelector((state, eventId) => {
    const event = processedEventSelector(state, eventId);
    if (!event) {
      return state.eventColor;
    }
    return resolveEventProperty({
      state,
      resourceId: event.resource,
      valueInEvent: event.color,
      getValueInResource: r => r.eventColor,
      valueInState: state.eventColor
    });
  }),
  isPropertyReadOnly: createSelectorMemoized(isEventReadOnlySelector, state => state.eventModelStructure, (isEventReadOnly, eventModelStructure, _eventId) => {
    if (isEventReadOnly) {
      return () => true;
    }
    return property => {
      if (eventModelStructure?.[property] && !eventModelStructure?.[property].setter) {
        return true;
      }
      return false;
    };
  }),
  processedEventList: createSelectorMemoized(state => state.eventIdList, state => state.processedEventLookup, (eventIds, processedEventLookup) => eventIds.map(id => processedEventLookup.get(id))),
  idList: createSelector(state => state.eventIdList),
  modelList: createSelector(state => state.eventModelList),
  modelLookup: createSelector(state => state.eventModelLookup),
  canDragEventsFromTheOutside: createSelector(state => state.canDragEventsFromTheOutside && !state.readOnly),
  canDropEventsToTheOutside: createSelector(state => state.canDropEventsToTheOutside && !state.readOnly),
  isDraggable: createSelector((state, eventId) => {
    if (isEventReadOnlySelector(state, eventId)) {
      return false;
    }
    const eventModelStructure = state.eventModelStructure;
    if (eventModelStructure?.start && !eventModelStructure?.start.setter) {
      return false;
    }
    if (eventModelStructure?.end && !eventModelStructure?.end.setter) {
      return false;
    }
    const processedEvent = processedEventSelector(state, eventId);
    if (!processedEvent) {
      return false;
    }
    return resolveEventProperty({
      state,
      resourceId: processedEvent.resource,
      valueInEvent: processedEvent.draggable,
      getValueInResource: r => r.areEventsDraggable,
      valueInState: state.areEventsDraggable
    });
  }),
  isResizable: createSelector((state, eventId, side) => {
    if (isEventReadOnlySelector(state, eventId)) {
      return false;
    }
    const eventModelStructure = state.eventModelStructure;
    if (side === 'start' && eventModelStructure?.start && !eventModelStructure?.start.setter) {
      return false;
    }
    if (side === 'end' && eventModelStructure?.end && !eventModelStructure?.end.setter) {
      return false;
    }
    const processedEvent = processedEventSelector(state, eventId);
    if (!processedEvent) {
      return false;
    }
    return resolveEventProperty({
      state,
      resourceId: processedEvent.resource,
      valueInEvent: getIsResizableFromProperty(processedEvent.resizable, side) ?? undefined,
      getValueInResource: r => getIsResizableFromProperty(r.areEventsResizable, side) ?? undefined,
      valueInState: getIsResizableFromProperty(state.areEventsResizable, side) ?? false
    });
  }),
  isRecurring: createSelector(processedEventSelector, state => state.recurringEventsPlugin, (event, recurringEventsPlugin, _eventId) => recurringEventsPlugin != null && Boolean(event?.dataTimezone.rrule))
};
function getIsResizableFromProperty(propertyValue, side) {
  if (propertyValue === undefined) {
    return null;
  }
  if (propertyValue === true) {
    return true;
  }
  if (propertyValue === false) {
    return false;
  }
  if (propertyValue === side) {
    return true;
  }

  // If the property is a specific side (e.g., 'start' or 'end') but doesn't match the current side,
  // return false because the property explicitly restricts resizing to a specific side.
  return false;
}

/**
 * Resolves an event property by checking (in order of priority):
 * 1. The event itself (`valueInEvent`)
 * 2. The resource hierarchy, child → parent → … (`getValueInResource`)
 * 3. The component-level state (`valueInState`)
 */
function resolveEventProperty(parameters) {
  const {
    state,
    resourceId,
    valueInEvent,
    getValueInResource,
    valueInState
  } = parameters;
  if (valueInEvent !== undefined) {
    return valueInEvent;
  }
  return resolveResourceProperty(state, resourceId, getValueInResource, valueInState);
}